/**
 * Centralized search placeholder strings.
 *
 * Every key is scoped to a specific view or feature.
 * Even when two keys share the same text today, keeping them separate
 * lets us update one without affecting the other if search fields change.
 */

const searchPlaceholder = (...fields: string[]) => {
  if (fields.length === 0) return "Search...";
  if (fields.length === 1) return `Search by ${fields[0]}...`;
  if (fields.length === 2) return `Search by ${fields[0]} or ${fields[1]}...`;
  const last = fields[fields.length - 1];
  const rest = fields.slice(0, -1).join(", ");
  return `Search by ${rest}, or ${last}...`;
};

export const SEARCH_PLACEHOLDERS = {
  // Main list views
  STACKS: searchPlaceholder("name", "ID", "labels"),
  MODULES: searchPlaceholder("name", "ID", "labels"),
  WORKER_POOLS: searchPlaceholder("name", "labels"),
  SPACES: searchPlaceholder("name", "ID"),
  CONTEXTS: searchPlaceholder("name", "ID", "labels"),
  POLICIES: searchPlaceholder("name", "labels"),
  BLUEPRINTS: searchPlaceholder("name", "ID", "labels"),
  NOTIFICATIONS: searchPlaceholder("title", "content"),
  INTEGRATIONS: searchPlaceholder("name"),
  PROJECTS: searchPlaceholder("name", "ID"),
  FLOWS_PROJECTS: searchPlaceholder("name"),
  PLUGINS: searchPlaceholder("name"),
  TEMPLATES: searchPlaceholder("name", "labels", "description"),
  REPOSITORIES: searchPlaceholder("name"),
  RESOURCES: searchPlaceholder("ID", "provider", "type"),
  SCANS: searchPlaceholder("name", "slug", "labels"),
  SCAN_RESOURCES: searchPlaceholder("name", "type"),
  WORKSPACES: searchPlaceholder("name"),

  // Settings pages
  USERS: searchPlaceholder("name"),
  ROLES: searchPlaceholder("name"),
  API_KEYS: searchPlaceholder("name", "creator", "ID"),
  GROUPS: searchPlaceholder("name"),
  MFA_KEYS: searchPlaceholder("owner", "name", "ID"),
  PERSONAL_MFA_KEYS: searchPlaceholder("name", "ID"),
  PERMISSIONS: searchPlaceholder("name"),

  // Registry
  PROVIDERS: searchPlaceholder("ID", "description", "labels"),
  GPG_KEYS: searchPlaceholder("ID", "name", "description"),

  // Module version views
  INPUTS: searchPlaceholder("name", "description"),
  OUTPUTS: searchPlaceholder("name", "description"),
  DEPENDENCIES: searchPlaceholder("name", "source", "version"),
  MODULE_RESOURCES: searchPlaceholder("name", "type"),
  CONSUMERS: searchPlaceholder("name"),

  // Worker pool detail views
  RUNS: searchPlaceholder("stack name"),
  WORKER_POOL_USED_BY: searchPlaceholder("name", "ID", "labels"),

  // UsedBy views
  ROLE_USED_BY: searchPlaceholder("name"),
  POLICY_USED_BY: searchPlaceholder("name"),
  VCS_USED_BY: searchPlaceholder("name", "ID", "labels"),
  INTEGRATION_USED_BY: searchPlaceholder("name"),

  // Integration group list (webhooks, VCS agent pools, etc.)
  INTEGRATION_GROUP: searchPlaceholder("name"),

  // Run detail views
  RUN_CHANGES: searchPlaceholder("name", "address", "resource type"),
  ANSIBLE_RUN_TASKS: searchPlaceholder("host name"),

  // Config management (Ansible)
  ANSIBLE_HOSTS: searchPlaceholder("host", "playbook", "role", "task", "stack"),
  STACK_ANSIBLE_HOSTS: searchPlaceholder("host", "task", "playbook", "role"),

  // User settings
  USER_INTEGRATIONS: searchPlaceholder("ID"),

  // AI chat
  AI_CHAT_HISTORY: searchPlaceholder(),

  // Stack dependencies dropdown
  STACK_DEPENDENCIES: searchPlaceholder("stack name"),

  // Special
  LOGS: searchPlaceholder(),
  DEFAULT: searchPlaceholder(),
} as const;
